import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ConceptCode } from '@tiendanube/common';
import { config } from 'App/components/ExternalServices/MercadoPago/config';
import { useForm, useScript } from 'commons/hooks';
import { logError } from 'commons/logger';
import { StatusType } from 'commons/types';
import { convertStatusTypeToObject } from 'commons/utils/convertStatusTypeToObject';
import { useGetCountry } from 'domains/Auth/hooks';
import usePayorderToPay from './usePayorderToPay';
import {
  CreditCardFormFields,
  formConfigMercadoPago,
  formCreditCardSchema,
  getCreditCardFormErrors,
  validateErrorCodesMercadoPago,
} from './utils';
import { usePostCreditCardPaymentMutation } from '../CheckoutApi';
import { CheckoutContext } from '../CheckoutUI';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const MercadoPago: any;

interface errorsInterface {
  code: string;
  message: string;
}

const initialValues: CreditCardFormFields = {
  cardNumber: '',
  cardholderName: '',
  expirationDate: '',
  expirationMonth: '',
  expirationYear: '',
  securityCode: '',
  identificationType: '',
  identificationNumber: '',
  identificationNumberParsed: '',
  cardholderEmail: '',
  installments: '',
  issuer: '',
};

function useCreditCardCheckout(includeTransactionFee = false) {
  const { setCreditCardPaymentData } = useContext(CheckoutContext);
  const [executeCreditCardPayment, { data: creditCardPaymentData }] =
    usePostCreditCardPaymentMutation();
  const {
    payorderToPay,
    conceptsToPay,
    status: payOrderStatus,
  } = usePayorderToPay();
  const [executionStatus, setExecutionStatus] = useState<StatusType>('idle');

  const status: StatusType =
    executionStatus === 'success'
      ? creditCardPaymentData?.successfulExecution
        ? 'success'
        : 'error'
      : executionStatus;
  const country = useGetCountry();

  const cleanStatus = useCallback(() => {
    setExecutionStatus('idle');
  }, [setExecutionStatus]);

  const statusMercadoPago = useScript(
    import.meta.env.VITE_MP_SDK_PATH as string,
    {
      removeOnUnmount: true,
    },
  );

  const [cardForm, setCardForm] = useState<any>(undefined);

  const [errorsMercadoPago, setErrorsMercadoPago] =
    useState<Partial<CreditCardFormFields>>(initialValues);

  const [setupStatus, setSetupStatus] = useState<StatusType>('loading');
  const isCardFormMountedRef = useRef(false);

  const onSubmit = useCallback(async () => {
    if (!cardForm) return;

    if (setupStatus !== 'success') {
      const message = `CardForm is not ready yet. Current setup status: ${setupStatus}`;
      await logError(message, new Error(message));
      setExecutionStatus('error');
      return;
    }

    setExecutionStatus('loading');
    if (!payorderToPay) {
      const message =
        'Trying to execute credit card payment without payorder to pay';
      await logError(message, new Error(message));
      setExecutionStatus('error');
      return;
    }

    setErrorsMercadoPago(initialValues);
    try {
      await cardForm.createCardToken();
    } catch (error) {
      setExecutionStatus('error');
      await logError('Error in execution of createCardToken', error);
      return;
    }
    const {
      token,
      issuerId,
      cardholderEmail,
      paymentMethodId,
      identificationNumber,
      identificationType,
    } = cardForm.getCardFormData();

    executeCreditCardPayment({
      payorderId: payorderToPay.id,
      token,
      issuerId,
      paymentMethodId,
      email: cardholderEmail,
      identificationNumber,
      identificationType,
      shouldActivateRecurrentPayment: true,
      shouldSubscribeTransactionFeesToRp: includeTransactionFee,
    })
      .unwrap()
      .then((acc) => {
        setCreditCardPaymentData(acc);
        setExecutionStatus('success');
      })
      .catch(() => {
        setCreditCardPaymentData(null);
        setExecutionStatus('error');
      });
  }, [
    cardForm,
    setupStatus,
    executeCreditCardPayment,
    payorderToPay,
    includeTransactionFee,
    setCreditCardPaymentData,
  ]);
  const {
    values: formValues,
    errors: formErrors,
    handleOnSubmit,
    handleOnChange,
    setFieldValue,
  } = useForm<CreditCardFormFields, Partial<CreditCardFormFields>>({
    initialValues,
    onSubmit,
    validationSchema: formCreditCardSchema(country),
  });

  useEffect(() => {
    if (statusMercadoPago === 'error' || payOrderStatus === 'error') {
      setSetupStatus('error');
    }
    if (
      statusMercadoPago === 'ready' &&
      payOrderStatus === 'success' &&
      !isCardFormMountedRef.current
    ) {
      const thereAreArgENConcepts = thereAreArgEnvioNubeConcepts(conceptsToPay);
      const pk = thereAreArgENConcepts
        ? config.pk.envioNube.AR
        : config.pk[country];
      const mp = new MercadoPago(pk);
      const cardForm = mp.cardForm({
        amount: '1000', // This amount is not actually used for the payment
        autoMount: true,
        form: formConfigMercadoPago(country),
        callbacks: {
          onFormMounted: (errors) => {
            if (errors) {
              setSetupStatus('error');
            }
          },
          onReady: () => {
            setSetupStatus('success');
            isCardFormMountedRef.current = true;
          },
          onCardTokenReceived: (errors: errorsInterface[]) => {
            if (errors) {
              setErrorsMercadoPago(validateErrorCodesMercadoPago(errors));
              logError(
                'Error creating Mercado Pago card token (onCardTokenReceived)',
                new Error(JSON.stringify(errors)),
              );
              return false;
            }
          },
          onSubmit: (event: SubmitEvent) => {
            event.preventDefault();
          },
        },
      });
      isCardFormMountedRef.current = true;
      setCardForm(cardForm);

      return () => {
        isCardFormMountedRef.current = false;
        try {
          cardForm.unmount();
        } catch {
          // Silently ignore unmount errors
        }
      };
    }
  }, [
    statusMercadoPago,
    payorderToPay,
    country,
    payOrderStatus,
    includeTransactionFee,
    conceptsToPay,
  ]);

  const errors = getCreditCardFormErrors(
    creditCardPaymentData?.errorCode,
    errorsMercadoPago,
    formErrors,
  );

  return {
    creditCardPaymentData,
    formErrors,
    handleOnChange,
    formValues,
    handleOnSubmit,
    setFieldValue,
    errors,
    setupStatusObject: convertStatusTypeToObject(setupStatus),
    status,
    cleanStatus,
    ...convertStatusTypeToObject(status),
  };
}

function thereAreArgEnvioNubeConcepts(
  conceptsToPay: Set<ConceptCode> | undefined,
): boolean {
  const argEnvioNubeConcepts: ConceptCode[] = [
    'ar-shipping-cost',
    'ar-shipping-credit',
    'ar-shipping-penalty',
    'ar-shipping-fee',
    'ar-shipping-credit-cost',
    'ar-shipping-credit-fee',
  ];

  return conceptsToPay
    ? argEnvioNubeConcepts.some((concept) => conceptsToPay.has(concept))
    : false;
}

export default useCreditCardCheckout;
